import PermissionChecker from 'src/modules/auth/permissionChecker';
import {tenantUserHasDataPermissionPrefix} from 'src/modules/data/dataSelectors';
import getStore from 'src/modules/store';
import React from 'react';
import {
  Redirect,
  Route,
  useLocation,
} from 'react-router-dom';
import Layout from 'src/view/layout/Layout';
import config from 'src/config';
import { tenantSubdomain } from 'src/modules/tenant/tenantSubdomain';
import { matchPermissionForUi } from 'src/security/customRoleScopedPermissionUi';
import { canAccessReportRealtimeRouteForUi } from 'src/security/reportRealtimeRouteAccess';

function PrivateRoute({
  component: Component,
  currentTenant,
  currentUser,
  permissionRequired,
  dataPermissionAccessPrefix,
  featureAccessAnyOf,
  componentProps,
  ...rest
}) {
  const location = useLocation();

  return (
    <Route
      {...rest}
      render={(props) => {
        const permissionChecker = new PermissionChecker(
          currentTenant,
          currentUser,
        );

        if (!permissionChecker.isAuthenticated) {
          return (
            <Redirect
              to={{
                pathname: '/auth/signin',
                state: { from: location },
              }}
            />
          );
        }

        if (!permissionChecker.isEmailVerified) {
          return <Redirect to="/auth/email-unverified" />;
        }

        if (
          ['multi', 'multi-with-subdomain'].includes(
            config.tenantMode,
          ) &&
          !tenantSubdomain.isSubdomain
        ) {
          if (permissionChecker.isEmptyTenant) {
            return <Redirect to="/auth/tenant" />;
          }
        } else {
          if (permissionChecker.isEmptyPermissions) {
            return (
              <Redirect to="/auth/empty-permissions" />
            );
          }
        }

        const authState = getStore()?.getState()?.auth;
        const featureKeys: string[] = authState?.tenantAccessContext?.featureKeys || [];
        const tenantAccessContextLoadStatus = authState?.tenantAccessContextLoadStatus;
        const hasBasePermission =
          matchPermissionForUi(
            permissionChecker,
            permissionRequired,
          ) ||
          tenantUserHasDataPermissionPrefix(
            currentUser,
            currentTenant,
            dataPermissionAccessPrefix,
          );
        const allowedByFeatures = canAccessReportRealtimeRouteForUi(
          hasBasePermission,
          featureAccessAnyOf,
          featureKeys,
          tenantAccessContextLoadStatus,
        );

        if (!allowedByFeatures) {
          return <Redirect to="/403" />;
        }

        return (
          <Layout {...props}>
            <Component {...props} {...(componentProps || {})} />
          </Layout>
        );
      }}
    />
  );
}

export default PrivateRoute;
